import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FeedbackDsButton, FeedbackDsModal } from 'src/components/feedback/feedback-design-system/feedback-ds-modal';
import { useGetSkuSuggestionsMutation } from 'src/redux/billing/billing.endpoints';
import {
  SKU_RULES_LOOKUP_ROWS,
  SkuSuggestion,
  SkuSuggestionContext,
} from './sku-helper.types';
import { SkuFlowChart } from './sku-flow-chart';

interface SkuHelperModalProps {
  open: boolean;
  onClose: () => void;
  context: SkuSuggestionContext;
  onApply: (sku: string | null) => void;
  skus?: Array<{ id: string; sku: string; name?: string | null }>;
}

const CONFIDENCE_STYLES: Record<SkuSuggestion['confidence'], string> = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-gray-200 text-gray-700',
};

import { resolveSkuIdFromSuggestion } from './sku-utils';

function missingContextFields(context: SkuSuggestionContext): string[] {
  const missing: string[] = [];
  if (context.charged == null) missing.push('charged status');
  if (context.managed == null) missing.push('managed status');
  if (context.is_rtr == null) missing.push('RTR flag');
  if (context.is_political == null) missing.push('political flag');
  if (!context.channel) missing.push('channel');
  if (!context.platform) missing.push('platform');
  return missing;
}

const SkuHelperModal: React.FC<SkuHelperModalProps> = ({
  open,
  onClose,
  context,
  onApply,
  skus = [],
}) => {
  const [fetchSuggestions, { data, isLoading, isError }] = useGetSkuSuggestionsMutation();
  const [rulesExpanded, setRulesExpanded] = useState(false);

  const contextKey = JSON.stringify(context);

  useEffect(() => {
    if (!open) return;
    fetchSuggestions(context);
    setRulesExpanded(false);
  }, [open, contextKey, fetchSuggestions]);

  const suggestions = data ?? [];
  const showEmptyState = !isLoading && (isError || suggestions.length === 0);
  const missingFields = useMemo(() => missingContextFields(context), [context]);

  useEffect(() => {
    if (showEmptyState) {
      setRulesExpanded(true);
    }
  }, [showEmptyState]);

  const handleApply = (suggestion: SkuSuggestion) => {
    const skuId = suggestion.sku_id ?? resolveSkuIdFromSuggestion(suggestion.sku, skus);
    if (suggestion.sku !== null && skuId === null) {
      window.alert(`SKU "${suggestion.sku}" was not found in the product catalogue. Sync SKUs or select manually.`);
      return;
    }
    onApply(skuId);
    onClose();
  };

  return (
    <FeedbackDsModal
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      title="SKU Helper"
      description="Suggested SKUs based on the current line item or campaign context."
    >
      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[#5E6278]">
          Suggestions
        </h3>

        {isLoading && (
          <p className="text-sm text-[#5E6278]">
            Loading suggestions…
          </p>
        )}

        {showEmptyState && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            No confident suggestions available.
            {missingFields.length > 0 && (
              <>
                {' '}
                Missing inputs:
                {' '}
                {missingFields.join(', ')}
                .
              </>
            )}
            {' '}
            Review the decision rules below.
          </div>
        )}

        {!isLoading && suggestions.length > 0 && (
          <ul className="space-y-2">
            {suggestions.map((suggestion) => (
              <li
                key={`${suggestion.sku ?? 'hide'}-${suggestion.label}`}
                className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[#0D0F1A]">
                        {suggestion.sku ?? '—'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${CONFIDENCE_STYLES[suggestion.confidence]}`}
                      >
                        {suggestion.confidence}
                      </span>
                    </div>
                    <p className="text-xs font-medium text-[#0D0F1A]">
                      {suggestion.label}
                    </p>
                    <p className="text-xs text-[#5E6278]">
                      {suggestion.rationale}
                    </p>
                  </div>
                  <FeedbackDsButton
                    variant="accent"
                    className="shrink-0 px-3 py-1.5 text-xs"
                    onClick={() => handleApply(suggestion)}
                  >
                    Apply
                  </FeedbackDsButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="pt-2">
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left text-sm font-semibold text-[#0D0F1A]"
          onClick={() => setRulesExpanded((prev) => !prev)}
        >
          {rulesExpanded ? (
            <ChevronDown className="size-4 shrink-0" />
          ) : (
            <ChevronRight className="size-4 shrink-0" />
          )}
          Decision rules reference
        </button>

        {rulesExpanded && (
          <div className="mt-3 max-h-[min(50vh,480px)] space-y-4 overflow-y-auto">
            <div className="overflow-x-auto rounded-lg border border-[#EDEEF2] bg-white p-2">
              <SkuFlowChart className="mx-auto h-auto w-full min-w-[520px] max-w-none" />
            </div>

            <div className="overflow-x-auto rounded-lg border border-[#EDEEF2]">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-[#F7F8FA] text-[#5E6278]">
                  <tr>
                    <th className="px-3 py-2 font-semibold">
                      Scenario
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      Bucket
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      SKU codes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SKU_RULES_LOOKUP_ROWS.map((row) => (
                    <tr
                      key={row.scenario}
                      className="border-t border-[#EDEEF2]"
                    >
                      <td className="px-3 py-2 text-[#0D0F1A]">
                        {row.scenario}
                      </td>
                      <td className="px-3 py-2 text-[#0D0F1A]">
                        {row.bucket}
                      </td>
                      <td className="px-3 py-2 text-[#5E6278]">
                        {row.codes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </FeedbackDsModal>
  );
};

export default SkuHelperModal;
